import classNames from "classnames";
import { lazy, Suspense, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";

// Hooks
import useClickInside from "hooks/useClickInside";
import useClickOutside from "hooks/useClickOutside";

import "./styles.sass";

// Icons
const LazySearchIcon = lazy(() => import("assets/icons/search.svg?react"));

type InputSearchProps = {
  name: string;
  mode?: "light" | "dark";
  placeholder?: string;
  gtm?: (keyword: string) => void;
  value: string;
  onChange?: (value, props: InputSearchProps) => void;
};

const InputSearch = (props: InputSearchProps) => {
  const {
    name,
    placeholder,
    mode = import.meta.env.VITE_APP_PROJECT_NAME === "banger"
      ? "dark"
      : "light",
    value,
    gtm,
    onChange,
  } = props;

  const [isFocus, setIsFocus] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const componentRef = useRef<HTMLDivElement | null>(null);

  const { control } = useFormContext();

  const { field: fieldController } = useController({
    control,
    name,
    defaultValue: value,
  });

  const handleBlur = () => {
    if (gtm) {
      gtm(fieldController.value);
    }
    inputRef.current?.blur();
    setIsFocus(false);
  };

  const handleFocus = () => {
    inputRef.current?.focus();
    setIsFocus(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (onChange) {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const { value } = e.target;
      fieldController.onChange(value);
      onChange(value, props);
    }
  };

  const handleReset = (e) => {
    e.preventDefault();
    if (gtm) {
      gtm(fieldController.value);
    }
    fieldController.onChange("");
    inputRef.current?.focus();
    // eslint-disable-next-line no-unused-expressions
    onChange && onChange("", props);
  };

  useClickInside({
    refs: [inputRef],
    handler: handleFocus,
    isDisable: isFocus,
  });
  useClickOutside({
    refs: [componentRef],
    handler: handleBlur,
    isDisable: !isFocus,
  });

  const clearClass = classNames("InputSearch__clear", {
    dark: mode === "dark",
  });

  return (
    <div className="InputSearch" ref={componentRef}>
      <div className="InputSearch__icon">
        <Suspense fallback="">
          <LazySearchIcon />
        </Suspense>
      </div>
      <input
        type="text"
        className="InputSearch__input"
        ref={(e) => {
          fieldController.ref(e);
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          inputRef.current = e;
        }}
        placeholder={placeholder}
        value={fieldController.value}
        // eslint-disable-next-line react/jsx-handler-names
        onChange={onChange ? handleChange : fieldController.onChange}
      />
      {isFocus && fieldController.value.length ? (
        // eslint-disable-next-line jsx-a11y/control-has-associated-label
        <button type="button" className={clearClass} onClick={handleReset} />
      ) : null}
    </div>
  );
};

export default InputSearch;
